import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Heading,
  Icon,
  Image,
  SimpleGrid,
  Text,
  VStack,
  HStack,
  Badge,
  Flex,
  IconButton
} from '@chakra-ui/react';
import {
  FaBook,
  FaChalkboardTeacher,
  FaUserGraduate,
  FaCertificate,
  FaVolumeUp,
  FaVolumeOff,
  FaPlay
} from 'react-icons/fa';
import { MdSchool, MdTrendingUp } from 'react-icons/md';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import Header from '../layout/Header';
import Footer from '../layout/Footer';
import axiosInstance from '../services/axiosInstance';

const MotionBox = motion(Box);

const CustomVideo = ({ src, badgeText, badgeColor }) => {
  console.log("Video URL (CustomVideo src):", src);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  // Handle play/pause events to sync state
  const onPlay = () => setIsPlaying(true);
  const onPause = () => setIsPlaying(false);

  const handleVideoClick = async (event) => {
    const video = videoRef.current;
    if (!video) return;
    try {
      if (video.paused) {
        await video.play();
      } else {
        await video.pause();
      }
    } catch (err) {
      console.warn('Video play/pause error:', err);
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    const video = videoRef.current;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  // Autoplay handling on mount to satisfy browser policies
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.muted = true;
      setIsMuted(true);
      video
        .play()
        .then(() => setIsPlaying(true))
        .catch((err) => console.warn('Autoplay failed:', err));
    }
  }, []);

  return (
    <Box 
      position="relative" 
      borderRadius="3xl" 
      overflow="hidden" 
      boxShadow="2xl" 
      w="100%" 
      h={{ base: '600px', md: '700px' }} 
      cursor="pointer"
      onClick={handleVideoClick}
      bg="black"
      transition="transform 0.3s"
      _hover={{ transform: 'scale(1.02)' }}
    >
      <video
        ref={videoRef}
        muted={isMuted}
        loop
        playsInline
        onPlay={onPlay}
        onPause={onPause}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      >
        <source src={src} type="video/mp4" />
      </video>
      
      {/* Play/Pause Overlay */}
      {!isPlaying && (
        <Flex
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          align="center"
          justify="center"
          bg="blackAlpha.500"
        >
          <Icon as={FaPlay} boxSize={16} color="whiteAlpha.900" />
        </Flex>
      )}

      {/* Bottom Gradient Overlay */}
      <Box
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        h="50%"
        bgGradient="linear(to-t, blackAlpha.900, transparent)"
        pointerEvents="none"
      />

      {/* Controls and Information */}
      <Flex
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        p={6}
        justify="space-between"
        align="flex-end"
      >
        <VStack align="start" spacing={3}>
          <Badge colorScheme={badgeColor} fontSize="md" px={4} py={1} borderRadius="full" textTransform="none">
            {badgeText}
          </Badge>
          <Text color="white" fontWeight="bold" fontSize="xl" noOfLines={2} textShadow="0px 2px 4px rgba(0,0,0,0.5)">
            {badgeText === 'الفيديو التقديمي' ? 'تعرف على المنصة' : `استمع إلى ${badgeText}`}
          </Text>
        </VStack>

        <IconButton
          icon={<Icon as={isMuted ? FaVolumeOff : FaVolumeUp} boxSize={6} />}
          onClick={toggleMute}
          size="lg"
          isRound
          colorScheme="whiteAlpha"
          bg="whiteAlpha.300"
          _hover={{ bg: "whiteAlpha.500" }}
          variant="solid"
          aria-label="Toggle Mute"
        />
      </Flex>
    </Box>
  );
};

const HomePage = () => {
  const navigate = useNavigate();
  const [isVideoMuted, setIsVideoMuted] = useState(true);
  const [feedbackVideos, setFeedbackVideos] = useState([]);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const { data } = await axiosInstance.get('/feedback-videos');
        setFeedbackVideos(data);
      } catch (error) {
        console.error('Error fetching feedback videos:', error);
      }
    };
    fetchVideos();
  }, []);

  const primaryColor = '#F2C919';
  const secondaryColor = '#2C3E50';

  const stats = [
    { number: '1500+', label: 'درس تعليمي', icon: FaBook },
    { number: '150+', label: 'حصة شهرياً', icon: FaChalkboardTeacher },
    { number: '20+', label: 'أستاذ متخصص', icon: FaUserGraduate },
    { number: '1200+', label: 'تلميذ ناجح', icon: MdSchool }
  ];

  const features = [
    {
      title: 'دروس مسجلة للتلاميذ',
      description: 'محتوى تعليمي شامل متوافق مع المنهج التونسي لجميع المستويات',
      icon: FaBook,
      color: primaryColor
    },
    {
      title: 'حصص مباشرة تفاعلية',
      description: 'تواصل مباشر مع الأساتذة لضمان فهم أفضل للدروس',
      icon: FaChalkboardTeacher,
      color: '#D69E2E'
    },
    {
      title: 'مواد تعليمية متنوعة',
      description: 'تمارين، ملخصات، وامتحانات لتعزيز التحصيل الدراسي',
      icon: MdSchool,
      color: '#ECC94B'
    },
    {
      title: 'متابعة تعليمية مستمرة',
      description: 'تقييم دوري لمستوى التلاميذ وتقارير تفصيلية للأولياء',
      icon: FaCertificate,
      color: '#F6AD55'
    }
  ];

  const getTypeLabel = (type) => {
    switch(type) {
      case 'parent': return 'رأي الولي';
      case 'student': return 'رأي التلميذ';
      case 'intro': return 'فيديو تقديمي';
      default: return 'فيديو المنصة';
    }
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'parent': return 'cyan';
      case 'student': return 'yellow';
      case 'intro': return 'purple';
      default: return 'blue';
    }
  };

  const innerSliderSettings = {
    dots: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    rtl: true,
    arrows: false,
    autoplay: true,
    autoplaySpeed: 5000,
  };

  const parentVideos = feedbackVideos.filter(v => v.type === 'parent');
  const studentVideos = feedbackVideos.filter(v => v.type === 'student');
  const introVideos = feedbackVideos.filter(v => v.type === 'intro');

  return (
    <Box dir="rtl" bg="gray.50">
      <Header />

      {/* Hero Section with Video */}
      <Box
        bgGradient={`linear(to-b, gray.900, ${secondaryColor})`}
        color="white"
        py={{ base: 12, md: 16 }}
        position="relative"
        overflow="hidden"
      >
        <Container maxW="1600px" position="relative" zIndex={1}>
          <Flex
            direction="column"
            align="center"
            justify="center"
          >
            <VStack spacing={4} mb={12} textAlign="center">
              <Heading fontSize={{ base: '3xl', md: '5xl' }} fontWeight="900" fontFamily="Tajawal, sans-serif">
                اكتشف منصة كوبرا
              </Heading>
              <Text fontSize={{ base: 'lg', md: 'xl' }} color="gray.300" maxW="800px" fontFamily="Tajawal, sans-serif">
                شاهد الفيديوهات لتعرف المزيد عن تجربتنا التعليمية من خلال آراء الأولياء والتلاميذ
              </Text>
            </VStack>

            {/* 3 Columns of Vertical TikTok-Style Sliders */}
            <Box w="100%" px={{ base: 2, md: 8 }} pb={10} css={{
              '.slick-dots li button:before': { color: 'white', fontSize: '10px' },
              '.slick-dots li.slick-active button:before': { color: '#F2C919' }
            }}>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
                
                {/* 1. Parent Feedback Column */}
                <Box>
                  {parentVideos.length > 0 ? (
                    <Slider {...innerSliderSettings} infinite={parentVideos.length > 1}>
                      {parentVideos.map((video, index) => (
                        <Box key={video._id || index} px={2}>
                          <CustomVideo 
                            src={`${axiosInstance.defaults.baseURL.replace('/api', '')}${video.videoUrl.replace(/^\/uploads/, '/uploads/videos')}`}
                            badgeText={getTypeLabel('parent')} 
                            badgeColor={getTypeColor('parent')} 
                          />
                        </Box>
                      ))}
                    </Slider>
                  ) : (
                    <Box px={2}>
                      <CustomVideo src="/assets/cobra-intro.mp4" badgeText={getTypeLabel('parent')} badgeColor={getTypeColor('parent')} />
                    </Box>
                  )}
                </Box>

                {/* 2. Student Feedback Column */}
                <Box>
                  {studentVideos.length > 0 ? (
                    <Slider {...innerSliderSettings} infinite={studentVideos.length > 1}>
                      {studentVideos.map((video, index) => (
                        <Box key={video._id || index} px={2}>
                          <CustomVideo 
                            src={`${axiosInstance.defaults.baseURL.replace('/api', '')}${video.videoUrl.replace(/^\/uploads/, '/uploads/videos')}`}
                            badgeText={getTypeLabel('student')} 
                            badgeColor={getTypeColor('student')} 
                          />
                        </Box>
                      ))}
                    </Slider>
                  ) : (
                    <Box px={2}>
                      <CustomVideo src="/assets/cobra-intro.mp4" badgeText={getTypeLabel('student')} badgeColor={getTypeColor('student')} />
                    </Box>
                  )}
                </Box>

                {/* 3. Intro Video Column */}
                <Box>
                  {introVideos.length > 0 ? (
                    <Slider {...innerSliderSettings} infinite={introVideos.length > 1}>
                      {introVideos.map((video, index) => (
                        <Box key={video._id || index} px={2}>
                          <CustomVideo 
                            src={`${axiosInstance.defaults.baseURL.replace('/api', '')}${video.videoUrl.replace(/^\/uploads/, '/uploads/videos')}`}
                            badgeText={getTypeLabel('intro')} 
                            badgeColor={getTypeColor('intro')} 
                          />
                        </Box>
                      ))}
                    </Slider>
                  ) : (
                    <Box px={2}>
                      <CustomVideo src="/assets/cobra-intro.mp4" badgeText={getTypeLabel('intro')} badgeColor={getTypeColor('intro')} />
                    </Box>
                  )}
                </Box>

              </SimpleGrid>
            </Box>
          </Flex>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box py={{ base: 16, md: 24 }} bg="white">
        <Container maxW="1400px">
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8}>
            {stats.map((stat, index) => (
              <MotionBox
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <VStack
                  spacing={4}
                  p={6}
                  bg="gray.50"
                  borderRadius="2xl"
                  transition="all 0.3s"
                  _hover={{ transform: 'translateY(-4px)', boxShadow: 'lg', bg: `${primaryColor}10` }}
                >
                  <Icon as={stat.icon} boxSize={10} color={primaryColor} />
                  <Heading
                    fontSize="4xl"
                    fontFamily="Tajawal, sans-serif"
                    fontWeight="900"
                    color={secondaryColor}
                  >
                    {stat.number}
                  </Heading>
                  <Text
                    fontSize="md"
                    fontFamily="Tajawal, sans-serif"
                    fontWeight="600"
                    color="gray.600"
                  >
                    {stat.label}
                  </Text>
                </VStack>
              </MotionBox>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box py={{ base: 16, md: 24 }} bg="gray.50">
        <Container maxW="1400px">
          <VStack spacing={4} mb={16} textAlign="center">
            <Heading
              fontSize={{ base: '3xl', md: '5xl' }}
              fontFamily="Tajawal, sans-serif"
              fontWeight="900"
              color={secondaryColor}
            >
              خدماتنا التعليمية
            </Heading>
            <Text
              fontSize={{ base: 'md', md: 'lg' }}
              color="gray.600"
              fontFamily="Tajawal, sans-serif"
              maxW="700px"
            >
              نوفر للتلاميذ كل ما يحتاجونه للنجاح الدراسي
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8}>
            {features.map((feature, index) => (
              <MotionBox
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -8 }}
              >
                <Box
                  bg="white"
                  p={8}
                  borderRadius="2xl"
                  boxShadow="lg"
                  transition="all 0.3s"
                  _hover={{ boxShadow: '2xl' }}
                  h="100%"
                  border="2px solid"
                  borderColor="gray.100"
                >
                  <VStack align="start" spacing={4}>
                    <Box
                      bg={`${feature.color}15`}
                      p={4}
                      borderRadius="xl"
                    >
                      <Icon as={feature.icon} boxSize={8} color={feature.color} />
                    </Box>
                    <Heading
                      fontSize="xl"
                      fontFamily="Tajawal, sans-serif"
                      fontWeight="800"
                      color={secondaryColor}
                    >
                      {feature.title}
                    </Heading>
                    <Text
                      fontSize="md"
                      color="gray.600"
                      fontFamily="Tajawal, sans-serif"
                      lineHeight="1.7"
                    >
                      {feature.description}
                    </Text>
                  </VStack>
                </Box>
              </MotionBox>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        bgGradient={`linear(to-l, ${secondaryColor}, gray.800)`}
        color="white"
        py={{ base: 16, md: 24 }}
      >
        <Container maxW="1000px" textAlign="center">
          <VStack spacing={8}>
            <Heading
              fontSize={{ base: '3xl', md: '5xl' }}
              fontFamily="Tajawal, sans-serif"
              fontWeight="900"
            >
              ابدأ رحلة التميز التعليمي اليوم
            </Heading>
            <Text
              fontSize={{ base: 'lg', md: 'xl' }}
              fontFamily="Tajawal, sans-serif"
              maxW="700px"
              lineHeight="1.8"
            >
              انضم إلى آلاف التلاميذ الذين يحققون النجاح الدراسي مع منصة كوبرا
            </Text>
            <HStack spacing={4} flexWrap="wrap" justify="center">
              <Button
                size="lg"
                bg={primaryColor}
                color={secondaryColor}
                _hover={{ bg: '#E0B817', transform: 'translateY(-2px)' }}
                fontFamily="Tajawal, sans-serif"
                fontWeight="800"
                px={12}
                py={7}
                borderRadius="xl"
                boxShadow="xl"
                onClick={() => navigate('/discover')}
              >
                استكشف المحتوى
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderColor="white"
                color="white"
                _hover={{ bg: 'whiteAlpha.200' }}
                fontFamily="Tajawal, sans-serif"
                fontWeight="700"
                px={12}
                py={7}
                borderRadius="xl"
                onClick={() => navigate('/contact')}
              >
                تواصل معنا
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default HomePage;
